import React, { useRef, useEffect, useMemo } from "react";
import {} from "./InputOTP.types";
const SPACER = " "; // Space character to maintain positions
const InputOTP = ({ length = 6, value = "", onChange, onComplete, autoFocus = true, disabled = false, error = false, className = "", containerStyle, inputStyle, placeholder = "", }) => {
    const inputsRef = useRef([]);
    // Parse value into array, treating spaces as empty
    const digits = useMemo(() => {
        const arr = [];
        for (let i = 0; i < length; i++) {
            const char = value[i];
            arr[i] = char && char !== SPACER ? char : "";
        }
        return arr;
    }, [value, length]);
    // Convert array to string with spaces for empty positions
    const toValue = (arr) => arr.map(d => d || SPACER).join("");
    // Update single digit
    const setDigit = (index, digit) => {
        const newDigits = [...digits];
        newDigits[index] = digit;
        onChange(toValue(newDigits));
    };
    // Focus helpers
    const focusInput = (index) => inputsRef.current[index]?.focus();
    // Auto-focus on mount
    useEffect(() => {
        if (autoFocus)
            focusInput(0);
    }, [autoFocus]);
    // Check completion
    const checkComplete = (arr) => {
        if (arr.every(d => d)) {
            onComplete?.(arr.join(""));
        }
    };
    const handleChange = (e, index) => {
        const input = e.target.value.replace(/\D/g, "");
        if (!input) {
            setDigit(index, "");
            return;
        }
        // Handle paste/multiple chars
        if (input.length > 1) {
            const newDigits = [...digits];
            for (let i = 0; i < input.length && index + i < length; i++) {
                newDigits[index + i] = input[i];
            }
            onChange(toValue(newDigits));
            focusInput(Math.min(index + input.length, length - 1));
            checkComplete(newDigits);
            return;
        }
        // Single digit
        const newDigits = [...digits];
        newDigits[index] = input;
        onChange(toValue(newDigits));
        if (index < length - 1)
            focusInput(index + 1);
        checkComplete(newDigits);
    };
    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !digits[index] && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        }
        if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        }
        if (e.key === "ArrowRight" && index < length - 1) {
            e.preventDefault();
            focusInput(index + 1);
        }
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
        if (!pasted)
            return;
        const newDigits = [...digits];
        for (let i = 0; i < pasted.length; i++) {
            newDigits[i] = pasted[i];
        }
        onChange(toValue(newDigits));
        focusInput(Math.min(pasted.length, length) - 1);
        checkComplete(newDigits);
    };
    const baseInputStyle = {
        width: 48,
        height: 48,
        fontSize: 22,
        textAlign: "center",
        border: `1px solid ${error ? "#ff4d4f" : "#d9d9d9"}`,
        borderRadius: 6,
        outline: "none",
        backgroundColor: disabled ? "#f5f5f5" : "#fff",
        cursor: disabled ? "not-allowed" : "text",
        ...inputStyle,
    };
    return (React.createElement("div", { style: { display: "flex", gap: 8, ...containerStyle }, className: className }, Array.from({ length }, (_, i) => (React.createElement("input", { key: `otp-position-${String(i)}`, ref: el => {
            inputsRef.current[i] = el;
        }, type: "text", inputMode: "numeric", maxLength: i === 0 ? length : 1, autoComplete: i === 0 ? "one-time-code" : "off", value: digits[i], placeholder: placeholder, disabled: disabled, onChange: e => handleChange(e, i), onKeyDown: e => handleKeyDown(e, i), onPaste: handlePaste, onFocus: e => e.target.select(), style: baseInputStyle })))));
};
InputOTP.defaultProps = {
    length: 6,
    value: "",
    onComplete: undefined,
    autoFocus: true,
    disabled: false,
    error: false,
    className: "",
    containerStyle: undefined,
    inputStyle: undefined,
    placeholder: "",
};
export default InputOTP;
